import { Formio } from 'formiojs';
import SelectComponent from 'formiojs/components/select/Select';

// Custom Select Component that extends the default Formio Select
class CustomSelectComponent extends SelectComponent {
  static schema(...extend) {
    return SelectComponent.schema({
      type: 'select',
      label: 'Select',
      key: 'select',
      ...extend
    });
  }

  static get builderInfo() {
    return {
      title: 'Select',
      icon: 'list',
      group: 'basic',
      documentation: '/userguide/#select',
      weight: 70,
      schema: CustomSelectComponent.schema()
    };
  }

  // Override the addOption method to handle disabled property
  addOption(value, label, attr = {}, group, id) {
    // Find the data value for this option
    const dataValue = this.component.data?.values?.find(v => String(v.value) === String(value));

    // If it has disabled property, add it to attributes
    if (dataValue && dataValue.disabled) {
      attr.disabled = true;
    }

    // Call parent addOption with modified attributes
    const result = super.addOption(value, label, attr, group, id);

    // The attributes above only reach the <option> of the html5 widget. Choices.js
    // builds its list from selectOptions, so flag the option there as well.
    if (attr.disabled) {
      const option = this.selectOptions[this.selectOptions.length - 1];

      if (option && String(option.value) === String(value)) {
        option.disabled = true;
      }
    }

    return result;
  }

  // Helper method to get translated error message
  getTranslatedErrorMessage() {
    const intl = this.root && this.root.options && this.root.options.intl;

    if (intl && intl.formatMessage) {
      return intl.formatMessage({ id: 'voting_no_votes_left' });
    }

    // Fallback to English if translation is not available
    return 'No votes left for this option. You have reached the maximum number of votes allowed.';
  }

  // Override checkValidity to add custom voting validation
  checkValidity(data, dirty, rowData, silentCheck) {
    // Call parent validation first
    const errors = super.checkValidity(data, dirty, rowData, silentCheck) || [];

    // Check voting availability if value is selected
    const value = this.dataValue;
    if (value && this.root && this.root.options && this.root.options.votingAvailability) {
      const availability = this.root.options.votingAvailability[this.component.key];

      if (availability) {
        const valueItem = availability.find(item => String(item.value) === String(value));

        if (valueItem && !valueItem.can_vote) {
          const errorMessage = this.getTranslatedErrorMessage();
          const error = {
            component: this.component,
            message: errorMessage,
            level: 'error'
          };
          errors.push(error);

          // Also set the error state on the component
          this.setCustomValidity(error.message, dirty);
        }
      }
    }

    return errors;
  }

  // Override setValue to trigger validation when value changes
  setValue(value, flags = {}) {
    // Check if this value is restricted before setting it
    if (value && this.root && this.root.options && this.root.options.votingAvailability) {
      const availability = this.root.options.votingAvailability[this.component.key];

      if (availability) {
        const valueItem = availability.find(item => String(item.value) === String(value));

        if (valueItem && !valueItem.can_vote) {
          // Get translated message
          const errorMessage = this.getTranslatedErrorMessage();

          // Don't set the value, show error instead
          this.setCustomValidity(errorMessage, true);
          this.addMessages([{
            message: errorMessage,
            level: 'error'
          }]);

          // Return without setting the value
          return false;
        }
      }
    }

    // Call parent setValue first
    const result = super.setValue(value, flags);

    // Trigger validation after value is set if not during initial render
    if (!flags.noValidate) {
      setTimeout(() => {
        this.checkValidity(null, true);
      }, 0);
    }

    return result;
  }

  // Override updateValue to ensure validation runs
  updateValue(value, flags = {}) {
    const result = super.updateValue(value, flags);

    // Run validation when value updates
    if (!flags.noValidate && !this.options.readOnly) {
      this.checkValidity(null, true);
    }

    return result;
  }

  // Override attach to add event listeners for disabled options
  attach(element) {
    const result = super.attach(element);

    // Add click listener to show error when disabled options are clicked
    if (this.refs && this.refs.selectContainer) {
      this.refs.selectContainer.addEventListener('click', (event) => {
        const target = event.target;

        // Check if clicking on a disabled option
        if (target.disabled || target.closest('[disabled]')) {
          event.preventDefault();
          event.stopPropagation();

          // Get translated error message
          const errorMessage = this.getTranslatedErrorMessage();

          // Show error message
          this.setCustomValidity(errorMessage, true);
          this.addMessages([{
            message: errorMessage,
            level: 'error'
          }]);
        }
      });
    }

    return result;
  }
}

// Override the default select component
Formio.registerComponent('select', CustomSelectComponent);

export default CustomSelectComponent;
